/**
 * MergedBuoyPanel - Merged View Dashboard Panel Component
 * 
 * SOLID Principle: Single Responsibility Principle (SRP)
 * Purpose: Renders the merged dashboard panel that shows data from 
 * multiple buoys combined into a single chart/table view.
 * 
 * Responsibilities:
 * - Manage expand/collapse sidebar state for merged view
 * - Render multiple sidebar controls (one per buoy)
 * - Coordinate merged data display
 * - Switch between merged graph and table views
 */

import React, { useState } from 'react';
import SensorChart from '../../solid-chart/SensorChartRefactored';
import DataTable from '../../DataTable';
import SidebarControls from '../../../sidebar/SidebarControls';
import styles from '../SensorDashboard.module.css';

export const MergedBuoyPanel = React.memo(({ 
  selectedBuoys,
  perBuoyAvailability,
  selectedSensors, 
  setSelectedSensors, 
  selectedFields, 
  setSelectedFields, 
  isLoading,
  viewMode,
  rawData,
  onToggleMerge,
  canMerge
}) => {
  const [sidebarExpanded, setSidebarExpanded] = useState(true);

  // Create per-buoy field selections for merged chart
  const perBuoySelectedFields = {};
  selectedBuoys.forEach(buoyId => {
    perBuoySelectedFields[buoyId] = selectedFields[buoyId] || [];
  });

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #dee2e6',
              background: 'white',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '500'
            }}
          >
            {sidebarExpanded ? '⛶ Expand Graph' : '◀ Show Controls'}
          </button>
          <h2 className={styles.cardTitle}>
            Merged View: {selectedBuoys.join(', ')}
          </h2>
        </div>
      </div>
      
      <div className={styles.cardContent}>
        {/* Multiple Sidebars */}
        {sidebarExpanded && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {selectedBuoys.map((buoyId) => {
              const { availableSensors = [], availableFieldsBySensor = {} } = perBuoyAvailability[buoyId] || {};
              
              return (
                <aside key={buoyId} className={styles.sidebar}>
                  <SidebarControls
                    buoyId={buoyId}
                    availableSensors={availableSensors}
                    availableFieldsBySensor={availableFieldsBySensor}
                    selectedSensors={selectedSensors[buoyId] || []}
                    setSelectedSensors={(sensors) => setSelectedSensors(prev => ({ ...prev, [buoyId]: sensors }))}
                    selectedFields={selectedFields[buoyId] || []}
                    setSelectedFields={(fields) => setSelectedFields(prev => ({ ...prev, [buoyId]: fields }))}
                    isLoading={isLoading}
                  />
                </aside>
              );
            })}
          </div>
        )}

        {/* Merged Chart/Table */}
        <div className={styles.contentArea}>
          {viewMode === 'graph' ? (
            <SensorChart
              rawData={rawData}
              perBuoySelectedFields={perBuoySelectedFields}
              selectedBuoys={selectedBuoys}
              onToggleMerge={onToggleMerge}
              canMerge={canMerge}
              isMerged={true}
              mergedMode={true}
              onPerBuoyFieldsChange={(updatedPerBuoyFields) => {
                // Convert per-buoy fields format back to selectedFields format
                const newSelectedFields = { ...selectedFields };
                Object.entries(updatedPerBuoyFields).forEach(([buoyId, fields]) => {
                  newSelectedFields[buoyId] = fields;
                });
                setSelectedFields(newSelectedFields);
              }}
            />
          ) : (
            <DataTable
              rawData={rawData}
              perBuoySelectedFields={perBuoySelectedFields}
              selectedBuoys={selectedBuoys}
            />
          )}
        </div>
      </div>
    </div>
  );
});

export default MergedBuoyPanel;